import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, LogOut, RefreshCw, ShoppingBag, MessageSquare, IndianRupee, Clock } from "lucide-react";
import axios from "axios";
import { API, formatINR } from "@/constants";

const EASE = [0.16, 1, 0.3, 1];

const INPUT_CLS =
  "h-12 w-full border border-forest/20 bg-transparent px-4 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:ring-1 focus:ring-forest transition-colors duration-300";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const authHeaders = () => {
  const token = localStorage.getItem("utsav_admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tab, setTab] = useState("orders");
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [o, e] = await Promise.all([
        axios.get(`${API}/admin/orders`, { withCredentials: true, headers: authHeaders() }),
        axios.get(`${API}/admin/enquiries`, { withCredentials: true, headers: authHeaders() }),
      ]);
      setOrders(o.data);
      setEnquiries(e.data);
    } catch {
      setUser(false);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true, headers: authHeaders() })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const markPaid = async (orderId) => {
    try {
      await axios.post(
        `${API}/admin/orders/${orderId}/mark-paid`,
        {},
        { withCredentials: true, headers: authHeaders() }
      );
      fetchData();
    } catch {
      // order already paid or request failed; refresh will show truth
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      localStorage.setItem("utsav_admin_token", data.token);
      setUser(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem("utsav_admin_token");
    setUser(false);
  };

  if (user === null) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-forest text-cream">
        <Loader2 size={32} className="animate-spin text-marigold" />
      </main>
    );
  }

  if (user === false) {
    return (
      <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-forest px-6 text-cream">
        <motion.form
          onSubmit={handleLogin}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="w-full max-w-md border border-cream/15 p-10 md:p-12"
        >
          <p className="font-serif text-4xl italic">Utsav <span className="not-italic text-marigold">Owner</span></p>
          <p className="mt-2 text-xs uppercase tracking-[0.25em] text-cream/50">Private dashboard</p>
          <div className="mt-10 space-y-5">
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-cream/50" htmlFor="admin-email">Email</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 w-full border border-cream/20 bg-transparent px-4 text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:ring-1 focus:ring-cream"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-cream/50" htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 w-full border border-cream/20 bg-transparent px-4 text-sm text-cream placeholder:text-cream/30 focus:outline-none focus:ring-1 focus:ring-cream"
                placeholder="••••••••"
              />
            </div>
          </div>
          {error && (
            <p className="mt-4 text-sm text-marigold" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-8 flex w-full items-center justify-center gap-3 bg-cream py-4 text-xs font-bold uppercase tracking-[0.25em] text-forest transition-colors duration-300 hover:bg-marigold disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </motion.form>
      </main>
    );
  }

  const paidOrders = orders.filter((o) => o.payment_status === "paid");
  const revenue = paidOrders.reduce((s, o) => s + (o.total || 0), 0);

  const stats = [
    { icon: IndianRupee, label: "Revenue (paid)", value: formatINR(revenue), testid: "stat-revenue" },
    { icon: ShoppingBag, label: "Paid orders", value: paidOrders.length, testid: "stat-paid-orders" },
    { icon: Clock, label: "Pending orders", value: orders.length - paidOrders.length, testid: "stat-pending" },
    { icon: MessageSquare, label: "Enquiries", value: enquiries.length, testid: "stat-enquiries" },
  ];

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-cream pt-28 text-forest">
      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-forest/10 pb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-sage">Utsav Flowers</p>
            <h1 className="mt-1 font-serif text-4xl italic md:text-5xl">Owner Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchData}
              data-testid="admin-refresh-button"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-forest/20 transition-colors hover:bg-forest hover:text-cream"
              aria-label="Refresh"
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-forest/20 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors hover:bg-forest hover:text-cream"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="border border-forest/15 p-6" data-testid={s.testid}>
              <s.icon size={18} className="text-marigold" />
              <p className="mt-4 font-serif text-3xl italic md:text-4xl">{s.value}</p>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-sage">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex gap-3" data-testid="admin-tabs">
          {["orders", "enquiries"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`rounded-full border px-6 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                tab === t
                  ? "border-forest bg-forest text-cream"
                  : "border-forest/20 text-forest/60 hover:border-forest hover:text-forest"
              }`}
            >
              {t} ({t === "orders" ? orders.length : enquiries.length})
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-x-auto border border-forest/15">
          {tab === "orders" ? (
            <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-orders-table">
              <thead className="border-b border-forest/15 bg-forest text-cream">
                <tr>
                  {["Date", "Order", "Customer", "Items", "Delivery", "Total", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center font-serif text-lg italic text-sage">No orders yet</td></tr>
                )}
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-forest/10 last:border-0" data-testid={`admin-order-row-${o.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 text-xs text-sage">{new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                    <td className="px-4 py-3 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold">{o.customer?.name}</p>
                      <p className="text-xs text-sage">{o.customer?.phone}</p>
                    </td>
                    <td className="px-4 py-3 text-xs">{(o.items || []).map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
                    <td className="px-4 py-3 text-xs">{[o.customer?.delivery_date, o.customer?.delivery_slot].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="px-4 py-3 font-serif text-base italic">{formatINR(o.total)}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${
                        o.payment_status === "paid" ? "bg-forest text-cream" : "bg-marigold/20 text-terracotta"
                      }`} data-testid={`admin-order-status-${o.id.slice(0, 8)}`}>
                        {o.payment_status === "awaiting_payment" ? "UPI pending" : o.payment_status}
                      </span>
                      {o.payment_status === "awaiting_payment" && (
                        <button
                          onClick={() => markPaid(o.id)}
                          data-testid={`admin-mark-paid-${o.id.slice(0, 8)}`}
                          className="mt-2 block border-b border-forest text-[10px] font-bold uppercase tracking-wider text-forest transition-colors hover:border-marigold hover:text-marigold"
                        >
                          Mark as paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-enquiries-table">
              <thead className="border-b border-forest/15 bg-forest text-cream">
                <tr>
                  {["Date", "Name", "Phone", "Occasion", "Message"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center font-serif text-lg italic text-sage">No enquiries yet</td></tr>
                )}
                {enquiries.map((q) => (
                  <tr key={q.id} className="border-b border-forest/10 last:border-0" data-testid={`admin-enquiry-row-${q.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 text-xs text-sage">{new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                    <td className="px-4 py-3 font-semibold">{q.name}</td>
                    <td className="px-4 py-3 text-xs">{q.phone}</td>
                    <td className="px-4 py-3 text-xs capitalize">{q.event_type || "—"}</td>
                    <td className="px-4 py-3 text-xs">{q.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );
}
